/**
 * Pack-switcher (Pattern C) per-card pricing — task 57525.
 *
 * Each pack-option card links to a DIFFERENT product and must show THAT product's
 * price. BSS's client-side price swap ignores the per-element bss-b2b-product-id on a
 * PDP and overwrites every price element with the PAGE product's price, so all cards
 * wrongly showed the current product's price (e.g. the 12-pak card showed the 1-stk
 * price). Fix: the bss-b2b-* hooks were removed from the cards in pack-switcher.liquid
 * so BSS no longer touches them; instead we mirror the discount ratio BSS applied to
 * the MAIN price (displayed / base) onto each card's own base price (data-base-price,
 * in cents). No hardcoded group %s -> no drift from BSS config. Guests: ratio 1 = base
 * price; farmer/vet: their discount. Re-runs as BSS swaps / on variant changes.
 */
(() => {
  /** "1.197,00 kr" -> 1197 (Danish: . thousands, , decimal). */
  const parsePrice = (text) => {
    const m = (text || '').match(/\d[\d.]*(?:,\d+)?/);
    if (!m) return null;
    const n = parseFloat(m[0].replace(/\./g, '').replace(',', '.'));
    return Number.isFinite(n) ? n : null;
  };

  const formatMoney = (n) =>
    n.toLocaleString('da-DK', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' kr';

  /**
   * The current product's VISIBLE main price element. A PDP has many
   * [bss-b2b-product-price] hooks (recommendation cards, sticky bar, hidden clones), so
   * we can't just take the first one — several are hidden (offsetParent null), which
   * would read no price and leave ratio=1. Pick a rendered element whose product id
   * matches the current product (from data-current-product-id); fall back to any visible
   * hook (the group discount is uniform, so any swapped price yields the same ratio).
   */
  const mainPriceEl = () => {
    const id = document.querySelector('.pack-switcher')?.dataset.currentProductId || null;
    const candidates = [...document.querySelectorAll('[bss-b2b-product-price]')].filter(
      (el) => !el.closest('.pack-switcher') && el.offsetParent !== null
    );
    return (id && candidates.find((el) => el.getAttribute('bss-b2b-product-id') === id)) || candidates[0] || null;
  };

  const leaves = (root) => [...root.querySelectorAll('*')].filter((el) => !el.children.length);

  /** Displayed selling price (skips struck compare-at). */
  const readDisplayed = (root) => {
    let v = null;
    leaves(root).forEach((el) => {
      const t = el.textContent.trim();
      if (!/kr/i.test(t) || !/\d/.test(t)) return;
      if (el.offsetParent === null) return;
      if (getComputedStyle(el).textDecorationLine.includes('line-through')) return;
      if (el.closest('.compare-at-price, .visually-hidden')) return;
      const n = parsePrice(t);
      if (n != null) v = n; // last valid = BSS's discounted price
    });
    return v;
  };

  /** Struck (original) price, if BSS/theme rendered one. */
  const readStruck = (root) => {
    let v = null;
    leaves(root).forEach((el) => {
      const t = el.textContent.trim();
      if (!/kr/i.test(t) || !/\d/.test(t)) return;
      if (!getComputedStyle(el).textDecorationLine.includes('line-through')) return;
      const n = parsePrice(t);
      if (n != null) v = n;
    });
    return v;
  };

  let observer = null;

  const refresh = () => {
    const cards = document.querySelectorAll('.pack-option__price[data-base-price]');
    if (!cards.length) return;
    observer?.disconnect();
    try {
      const main = mainPriceEl();
      // Mid-swap: BSS hides the main price container while re-rendering -> wait for it.
      if (main && getComputedStyle(main).visibility === 'hidden') return;

      let ratio = 1;
      if (main) {
        const displayed = readDisplayed(main);
        const struck = readStruck(main);
        const base = struck != null ? struck : displayed;
        if (displayed != null && base) ratio = displayed / base;
      }
      if (!(ratio > 0) || ratio > 1) ratio = 1; // no discount / sanity clamp

      cards.forEach((el) => {
        const baseCents = parseInt(el.dataset.basePrice, 10);
        if (!Number.isFinite(baseCents)) return;
        const baseKr = baseCents / 100;
        const html =
          ratio < 0.999
            ? `<span class="pack-option__was">${formatMoney(baseKr)}</span>` +
              `<span class="pack-option__now">${formatMoney(baseKr * ratio)}</span>`
            : formatMoney(baseKr);
        if (el.innerHTML !== html) el.innerHTML = html;
      });
    } finally {
      observe();
    }
  };

  let debounceId = null;
  const schedule = () => {
    clearTimeout(debounceId);
    debounceId = setTimeout(refresh, 120);
  };

  const observe = () =>
    observer?.observe(document.body, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['style', 'class', 'bss-b2b-product-active'],
      characterData: true,
    });

  const start = () => {
    observer = new MutationObserver(schedule);
    refresh();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
